import { useMutation } from '@apollo/client';
import { Button, Stack } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { WidgetIcon } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { I18nNamespace, NegotiationDomainType } from '~/common/types';
import { DataGridContextProvider } from '~/components/DataGridCustom/hooks/useDataGridContext/useDataGridContext';
import { DataGridQueryBuilder, useDataGridQueryBuilderQuery } from '~/components/DataGridQueryBuilder';
import { DataGridWidget } from '~/components/DataGridWidget';
import { DomainIcon } from '~/components/DomainIcon/DomainIcon';
import { Filters, FilterSaveStatus, useFilters } from '~/components/Filters';
import { ModulePageHeader } from '~/components/ModulePageHeader/ModulePageHeader';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { RouteId } from '~/routes';

import { useContractDemandDataGridConfig } from './ContractDemandList.dataGrid';
import { useDemandDataGrid } from './ContractDemandList.hooks';
import { CONTRACT_DEMAND_CREATE } from './ContractDemandList.mutation.gql';
import { CONTRACT_DEMAND_LIST_QUERY } from './ContractDemandList.query.gql';
import { useContractDemandDataGridQueryBuilderConfig } from './ContractDemandList.queryBuilder';

const domain = NegotiationDomainType.CONTRACT_DEMAND;

export const ContractDemandList = () => {
  const apiRef = useGridApiRef();
  const { queryId, query, onQueryChange, onQueryIdChange, rulesTotal, loading } = useDataGridQueryBuilderQuery({
    domain,
  });
  const { hasPermission } = usePermissions();
  const { t } = useTranslation(I18nNamespace.common, { keyPrefix: 'ContractDemandList' });
  const { onFilterSave, saveStatus, selected, ...filtersProps } = useFilters({ domain, queryId });
  const navigate = useNavigate();
  const [createContractDemand, { loading: createLoading }] = useMutation(CONTRACT_DEMAND_CREATE);
  const { contextValue } = useDemandDataGrid({ apiRef });
  return (
    <PageLayout
      fullHeight
      renderHeader={
        <>
          <ModulePageHeader
            title={t('title')}
            icon={<WidgetIcon icon={<DomainIcon domain={domain} />} />}
            right={
              <Stack
                spacing={3}
                direction="row"
                sx={{
                  overflow: 'hidden',
                  alignItems: 'center',
                }}
              >
                <Filters
                  {...filtersProps}
                  selected={selected}
                  onFilterSelect={(filter) => {
                    onQueryIdChange(filter.queryId);
                  }}
                />

                <Button
                  disabled={!hasPermission(Permission.ContractDemandCreate) || createLoading}
                  loading={createLoading}
                  onClick={async () => {
                    const result = await createContractDemand();
                    const id = result.data?.tesseract.contractDemandCreate.id;
                    if (id) {
                      navigate(RouteId.contractDemandDetail(id));
                    }
                  }}
                >
                  {t('button.createContractDemand')}
                </Button>
              </Stack>
            }
          />
          <DataGridQueryBuilder
            query={query}
            clearDisabled={rulesTotal === 0}
            saveDisabled={saveStatus === FilterSaveStatus.DISABLED}
            onQueryChange={onQueryChange}
            onSave={onFilterSave}
            selectedFilterName={selected?.name}
            useConfig={useContractDemandDataGridQueryBuilderConfig}
          />
        </>
      }
    >
      <DataGridContextProvider value={contextValue}>
        <DataGridWidget
          apiRef={apiRef}
          domain={domain}
          loading={loading}
          filter={query}
          filterCount={rulesTotal}
          useConfig={useContractDemandDataGridConfig}
          initialNavigateDetail={({ row }) => ({
            to: RouteId.contractDemandDetail(row.id),
            trackingOptions: { domain, id: row.id },
          })}
          loadingProps={{
            query: CONTRACT_DEMAND_LIST_QUERY,
            getNodes: (data) => data.tesseract.contractDemands.nodes,
            getTotalCount: (data) => data.tesseract.contractDemands.totalCount || 0,
          }}
        />
      </DataGridContextProvider>
    </PageLayout>
  );
};
